import { useEffect, useRef, useCallback, useState } from 'react';
import paymentService from '../services/payment.service';

/**
 * Custom hook to poll payment status
 * @param {number} invoiceId - Invoice ID to poll
 * @param {object} options - Polling options
 * @param {number} options.interval - Polling interval in ms (default: 5000)
 * @param {number} options.maxDuration - Maximum polling duration in ms (default: 900000 = 15 minutes)
 * @param {function} options.onSuccess - Callback when payment succeeds
 * @param {function} options.onFailure - Callback when payment fails/expires
 * @param {function} options.onTimeout - Callback when polling times out
 * @param {function} options.onError - Callback when API call fails
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 */
export const usePaymentPolling = (invoiceId, options = {}) => {
  const {
    interval = 5000, // 5 seconds
    maxDuration = 900000, // 15 minutes
    onSuccess,
    onFailure,
    onTimeout,
    onError,
    enabled = true
  } = options;

  const [status, setStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const isMountedRef = useRef(true);
  const consecutiveErrorsRef = useRef(0);

  // Store callbacks in refs to avoid recreating pollStatus on every callback change
  const callbacksRef = useRef({ onSuccess, onFailure, onTimeout, onError });

  // Update callback refs when they change (without triggering re-renders)
  useEffect(() => {
    callbacksRef.current = { onSuccess, onFailure, onTimeout, onError };
  }, [onSuccess, onFailure, onTimeout, onError]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const pollStatus = useCallback(async () => {
    if (!isMountedRef.current || !invoiceId) return;

    try {
      const response = await paymentService.getPaymentStatus(invoiceId);

      if (!isMountedRef.current) return; // Component unmounted during API call

      consecutiveErrorsRef.current = 0; // Reset error counter
      setStatus(response.data);
      setError(null);

      const { invoice_status, needs_polling } = response.data;

      // Stop polling if payment completed/failed/cancelled
      if (invoice_status === 'paid') {
        stopPolling();
        callbacksRef.current.onSuccess?.();
      } else if (['failed', 'cancelled'].includes(invoice_status)) {
        stopPolling();
        callbacksRef.current.onFailure?.(invoice_status);
      } else if (!needs_polling) {
        // Backend says no need to poll (e.g., payment link expired and webhook already processed)
        stopPolling();
      }

      // Check if max duration exceeded
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed >= maxDuration) {
        stopPolling();
        callbacksRef.current.onTimeout?.();
      }
    } catch (err) {
      console.error('Payment polling error:', err);

      if (!isMountedRef.current) return;

      consecutiveErrorsRef.current++;
      setError(err);

      // Stop polling after 3 consecutive errors to avoid hammering the server
      if (consecutiveErrorsRef.current >= 3) {
        stopPolling();
        callbacksRef.current.onError?.(err);
      }
    }
  }, [invoiceId, maxDuration, stopPolling]);

  // Start polling
  useEffect(() => {
    if (!enabled || !invoiceId) {
      stopPolling();
      return;
    }

    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    isMountedRef.current = true;
    startTimeRef.current = Date.now();
    setIsPolling(true);

    console.log(`🔄 Starting payment polling for invoice ${invoiceId} (interval: ${interval}ms)`);

    // Poll immediately
    pollStatus();

    // Then poll at interval
    intervalRef.current = setInterval(() => {
      console.log(`⏰ Polling payment status for invoice ${invoiceId}...`);
      pollStatus();
    }, interval);

    // Cleanup on unmount or when dependencies change
    return () => {
      console.log(`🛑 Stopping payment polling for invoice ${invoiceId}`);
      isMountedRef.current = false;
      stopPolling();
    };
  }, [enabled, invoiceId, interval, pollStatus, stopPolling]);

  return {
    status,
    isPolling,
    error,
    stopPolling
  };
};

export default usePaymentPolling;
